import { useEffect, useState, type ChangeEvent, type FormEvent } from "react";
import { Link, Route, Routes, useLocation } from "react-router-dom";

import AboutJason from "./about-jason";
import ContactPage from "./contact";
import DonatePage from "./donate";
import ElectionInformationPage from "./election-information";
import EventsPage from "./endorsements";
import PrivacyPolicyPage from "./privacy-policy";
import TermsAndConditionsPage from "./terms-and-conditions";

const campaign = {
  email: "info@votejasonhardcastle.org",
  location: "Galveston, TX, USA",
  facebookUrl: "https://www.facebook.com/profile.php?id=100068821106074",
  galvestonVotesUrl: "http://galvestonvotes.org",
};

const images = {
  logo: "/assets/hardcastle/hardcastle-d6-logo.jpg",
  portrait: "/assets/hardcastle/jason-hardcastle-portrait.jpg",
  neighborhood: "/assets/hardcastle/neighborhood-chat.jpg",
  districtMap: "/assets/hardcastle/district-6-map.png",
  districtMapWide: "/assets/hardcastle/district-6-map-wide.png",
  blueLogo: "/assets/hardcastle/hardcastle-d6-blue.jpg",
};

const navItems = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Election Info", to: "/election-information" },
  { label: "Events", to: "/events" },
  { label: "Contact", to: "/contact" },
  { label: "Donate", to: "/donate" },
];

const featureCards = [
  {
    title: "Experience that knows Galveston",
    body: "Jason has served as a councilperson, Park Board trustee, and Park Board chair while raising his family on the island.",
    image: images.portrait,
    alt: "Jason Hardcastle",
    to: "/about",
  },
  {
    title: "Neighborhood conversations",
    body: "The campaign is built around listening closely to District 6 residents and showing up where people already gather.",
    image: images.neighborhood,
    alt: "Neighborhood chat graphic",
    to: "/events",
  },
  {
    title: "Know your district",
    body: "District 6 stretches from Harve Lafitte toward the West End. Always verify your district and precinct before voting.",
    image: images.districtMap,
    alt: "District 6 map",
    to: "/election-information",
  },
];

const visionItems = [
  "Experienced leadership rooted in more than 20 years of Galveston service.",
  "Meaningful engagement so every District 6 resident has a voice.",
  "A stronger community that protects Galveston's heritage, neighborhoods, and future.",
];

function App() {
  return (
    <div className="min-h-screen bg-[#f7f1e6] text-[#112235]" style={{ fontFamily: "Montserrat, sans-serif" }}>
      <style>
        {`@import url('https://fonts.googleapis.com/css2?family=Libre+Baskerville:wght@400;700&family=Montserrat:wght@400;500;600;700;800&display=swap');`}
      </style>
      <ScrollToTop />
      <Header />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/about" element={<AboutJason />} />
        <Route path="/election-information" element={<ElectionInformationPage />} />
        <Route path="/events" element={<EventsPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/donate" element={<DonatePage />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/blank" element={<PrivacyPolicyPage />} />
        <Route path="/blank-1" element={<TermsAndConditionsPage />} />
      </Routes>
      <Footer />
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);

  return null;
}

function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;
  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <>
      <header className="sticky top-0 z-50 border-b-4 border-[#c0352b] bg-[#0b2f58] shadow-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between p-4 sm:px-6 lg:px-8">
          <Link to="/" className="text-sm font-extrabold uppercase tracking-[0.28em] text-white" onClick={closeMobileMenu}>
            Hardcastle D6
          </Link>

          <nav className="hidden md:block">
            <ul className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.12em]">
              {navItems.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className={`rounded-full px-4 py-2 transition ${
                      isActive(item.to)
                        ? "bg-white text-[#0b2f58]"
                        : "text-white hover:bg-white/15 hover:text-[#f7d77a]"
                    }`}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <button
            type="button"
            onClick={() => setMobileMenuOpen((isOpen) => !isOpen)}
            className="rounded-full border border-white/40 p-2 text-white md:hidden"
            aria-label="Toggle mobile menu"
          >
            {mobileMenuOpen ? (
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="size-6">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="size-6">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>

        {mobileMenuOpen && (
          <nav className="border-t border-white/20 bg-[#08213d] p-4 md:hidden">
            <ul className="space-y-2 text-center text-sm font-bold uppercase tracking-[0.14em]">
              {navItems.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    onClick={closeMobileMenu}
                    className={`block rounded-full px-4 py-3 ${
                      isActive(item.to) ? "bg-white text-[#0b2f58]" : "text-white hover:bg-white/15"
                    }`}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        )}
      </header>

      <div className="bg-white px-4 py-5 shadow-sm">
        <Link to="/" className="mx-auto block max-w-3xl">
          <img src={images.logo} alt="Jason Hardcastle for Galveston City Council District 6" className="mx-auto max-h-36 w-full object-contain" />
        </Link>
      </div>
    </>
  );
}

function HomePage() {
  return (
    <main>
      <section className="relative isolate overflow-hidden bg-[#f7f1e6]">
        <div className="absolute -left-32 top-10 size-72 rounded-full bg-[#d64a3a]/20 blur-3xl" />
        <div className="absolute -right-24 bottom-0 size-80 rounded-full bg-[#0b6a88]/20 blur-3xl" />

        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-[1.05fr_0.95fr] lg:px-8 lg:py-24">
          <div>
            <p className="mb-5 inline-flex rounded-full bg-[#c0352b] px-5 py-2 text-sm font-extrabold uppercase tracking-[0.18em] text-white shadow-lg">
              Galveston City Council District 6
            </p>
            <h1 className="text-5xl font-bold leading-tight text-[#08213d] sm:text-6xl lg:text-7xl" style={{ fontFamily: "Libre Baskerville, serif" }}>
              Experience and leadership for the island we call home.
            </h1>
            <p className="mt-7 max-w-2xl text-lg leading-8 text-[#294259]">
              Jason Hardcastle is running to represent District 6 with the same hands-on community service, practical leadership, and love for Galveston that have shaped his work for more than 20 years.
            </p>
            <div className="mt-9 flex flex-col gap-4 sm:flex-row">
              <Link to="/about" className="rounded-full bg-[#0b2f58] px-8 py-4 text-center text-sm font-extrabold uppercase tracking-[0.16em] text-white shadow-xl transition hover:bg-[#143f70]">
                Meet Jason
              </Link>
              <Link to="/contact" className="rounded-full border-2 border-[#0b2f58] bg-white px-8 py-4 text-center text-sm font-extrabold uppercase tracking-[0.16em] text-[#0b2f58] shadow-xl transition hover:bg-[#f7d77a]">
                Get Involved
              </Link>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -right-4 -top-4 size-full rounded-[2rem] bg-[#c0352b]" />
            <img src={images.portrait} alt="Jason Hardcastle" className="relative z-10 w-full rounded-[2rem] border-8 border-white object-cover shadow-2xl" />
            <div className="relative z-20 mx-6 -mt-10 rounded-2xl bg-white p-5 text-center shadow-2xl">
              <p className="text-sm font-extrabold uppercase tracking-[0.18em] text-[#c0352b]">Election Day</p>
              <p className="mt-1 text-2xl font-black text-[#08213d]">Saturday, May 2, 2026</p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="mb-10 flex flex-col justify-between gap-4 md:flex-row md:items-end">
            <div>
              <p className="font-bold uppercase tracking-[0.2em] text-[#c0352b]">Organized around the people</p>
              <h2 className="mt-3 text-4xl font-bold text-[#08213d]" style={{ fontFamily: "Libre Baskerville, serif" }}>
                Campaign essentials, all in one place
              </h2>
            </div>
            <Link to="/donate" className="rounded-full bg-[#c0352b] px-7 py-3 text-center text-sm font-extrabold uppercase tracking-[0.16em] text-white shadow-lg transition hover:bg-[#9f261f]">
              Donate
            </Link>
          </div>

          <div className="grid gap-7 md:grid-cols-3">
            {featureCards.map((card) => (
              <Link key={card.title} to={card.to} className="group overflow-hidden rounded-[1.75rem] bg-[#f7f1e6] shadow-lg ring-1 ring-black/5 transition hover:-translate-y-1 hover:shadow-2xl">
                <div className="h-64 overflow-hidden bg-[#d9e4ea]">
                  <img src={card.image} alt={card.alt} className="size-full object-cover transition duration-500 group-hover:scale-105" />
                </div>
                <div className="p-7">
                  <h3 className="text-2xl font-black text-[#08213d]">{card.title}</h3>
                  <p className="mt-4 leading-7 text-[#37516a]">{card.body}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#08213d] px-4 py-16 text-white sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
          <div>
            <p className="font-bold uppercase tracking-[0.2em] text-[#f7d77a]">Our Vision</p>
            <h2 className="mt-3 text-4xl font-bold" style={{ fontFamily: "Libre Baskerville, serif" }}>
              Stronger neighborhoods, steady leadership, and a city that listens.
            </h2>
            <p className="mt-6 text-lg leading-8 text-white/80">
              Jason Hardcastle for Galveston City Council District 6 is committed to meaningful engagement with voters and practical leadership for the community&apos;s next chapter.
            </p>
          </div>
          <div className="grid gap-4">
            {visionItems.map((item, index) => (
              <div key={item} className="rounded-2xl border border-white/15 bg-white/10 p-6 shadow-lg backdrop-blur">
                <p className="text-sm font-black uppercase tracking-[0.18em] text-[#f7d77a]">0{index + 1}</p>
                <p className="mt-3 text-xl font-bold leading-8">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[1fr_0.95fr] lg:items-start">
          <SignupPanel />

          <div className="rounded-[2rem] bg-white p-8 shadow-xl">
            <p className="font-bold uppercase tracking-[0.2em] text-[#c0352b]">Am I in District 6?</p>
            <h2 className="mt-3 text-3xl font-bold text-[#08213d]" style={{ fontFamily: "Libre Baskerville, serif" }}>
              Check your district and precinct before voting.
            </h2>
            <p className="mt-5 leading-7 text-[#37516a]">
              District 6 stretches from Harve Lafitte all the way to the West End. Use the maps here as a guide, then verify your district and precinct with Galveston Votes.
            </p>
            <img src={images.districtMapWide} alt="Galveston District 6 map" className="mt-7 w-full rounded-2xl border border-[#d7c9b8] bg-[#f7f1e6] object-cover" />
            <a href={campaign.galvestonVotesUrl} target="_blank" rel="noopener noreferrer" className="mt-7 inline-block rounded-full bg-[#0b2f58] px-7 py-3 text-sm font-extrabold uppercase tracking-[0.16em] text-white transition hover:bg-[#143f70]">
              Visit GalvestonVotes.org
            </a>
          </div>
        </div>
      </section>

      <section className="bg-white px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-8 rounded-[2rem] bg-[#f7f1e6] p-8 shadow-xl lg:grid-cols-[0.85fr_1.15fr] lg:p-10">
          <img src={images.blueLogo} alt="Jason Hardcastle District 6 campaign mark" className="mx-auto max-h-96 rounded-3xl object-contain" />
          <div className="flex flex-col justify-center">
            <p className="font-bold uppercase tracking-[0.2em] text-[#c0352b]">Reach out to us</p>
            <h2 className="mt-3 text-4xl font-bold text-[#08213d]" style={{ fontFamily: "Libre Baskerville, serif" }}>
              Questions, sign requests, and volunteer help all start here.
            </h2>
            <p className="mt-5 text-lg leading-8 text-[#37516a]">
              Send the campaign a message, request a sign, or let us know where Jason should stop by next in District 6.
            </p>
            <div className="mt-8 flex flex-col gap-4 sm:flex-row">
              <Link to="/contact" className="rounded-full bg-[#0b2f58] px-8 py-4 text-center text-sm font-extrabold uppercase tracking-[0.16em] text-white shadow-lg transition hover:bg-[#143f70]">
                Contact the campaign
              </Link>
              <a href={`mailto:${campaign.email}`} className="rounded-full border-2 border-[#0b2f58] px-8 py-4 text-center text-sm font-extrabold uppercase tracking-[0.16em] text-[#0b2f58] transition hover:bg-white">
                {campaign.email}
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

type SignupForm = {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
};

const initialSignupForm: SignupForm = {
  email: "",
  firstName: "",
  lastName: "",
  phone: "",
};

function SignupPanel() {
  const [form, setForm] = useState<SignupForm>(initialSignupForm);
  const [phoneConsent, setPhoneConsent] = useState(false);
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setForm((current) => {
      switch (name) {
        case "email": {
          return { ...current, email: value };
        }
        case "firstName": {
          return { ...current, firstName: value };
        }
        case "lastName": {
          return { ...current, lastName: value };
        }
        case "phone": {
          return { ...current, phone: value };
        }
        default: {
          return current;
        }
      }
    });
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setStatus("submitting");

    try {
      const data = new URLSearchParams();
      for (const [key, value] of Object.entries(form)) {
        data.append(key, value);
      }
      data.append("campaign", "Jason Hardcastle for Galveston City Council District 6");
      data.append("source", "homepage-catch-the-wave");
      data.append("phoneConsent", phoneConsent.toString());

      const response = await fetch("https://api.southcoaststrategy.com", {
        method: "POST",
        body: data,
        headers: {
          "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
        },
      });

      if (!response.ok) {
        throw new Error("Signup request failed.");
      }

      setForm(initialSignupForm);
      setPhoneConsent(false);
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  const submitDisabled = status === "submitting" || !form.email.trim();

  return (
    <div className="rounded-[2rem] bg-white p-8 shadow-xl">
      <p className="font-bold uppercase tracking-[0.2em] text-[#c0352b]">Catch the Wave</p>
      <h2 className="mt-3 text-4xl font-bold text-[#08213d]" style={{ fontFamily: "Libre Baskerville, serif" }}>
        Stay connected with the campaign.
      </h2>
      <form className="mt-8 grid gap-4" onSubmit={handleSubmit}>
        <input name="email" type="email" required value={form.email} onChange={handleChange} placeholder="Email address" className="rounded-xl border border-[#d7c9b8] bg-[#fffbf4] p-4 text-[#112235] outline-none ring-[#0b6a88] transition focus:ring-2" />
        <div className="grid gap-4 sm:grid-cols-2">
          <input name="firstName" value={form.firstName} onChange={handleChange} placeholder="First name" className="rounded-xl border border-[#d7c9b8] bg-[#fffbf4] p-4 text-[#112235] outline-none ring-[#0b6a88] transition focus:ring-2" />
          <input name="lastName" value={form.lastName} onChange={handleChange} placeholder="Last name" className="rounded-xl border border-[#d7c9b8] bg-[#fffbf4] p-4 text-[#112235] outline-none ring-[#0b6a88] transition focus:ring-2" />
        </div>
        <input name="phone" type="tel" value={form.phone} onChange={handleChange} placeholder="Phone (optional for SMS updates)" className="rounded-xl border border-[#d7c9b8] bg-[#fffbf4] p-4 text-[#112235] outline-none ring-[#0b6a88] transition focus:ring-2" />

        <label className="flex gap-3 rounded-2xl bg-[#f7f1e6] p-4 text-sm leading-6 text-[#37516a]">
          <input type="checkbox" checked={phoneConsent} onChange={() => setPhoneConsent((current) => !current)} className="mt-1 size-5 rounded border-[#0b2f58] text-[#0b2f58]" />
          <span>
            Optional SMS/MMS political text opt-in: By checking this box and submitting this form, I consent to receive recurring manually sent political text messages, including SMS and MMS, from Jason Hardcastle for Galveston City Council District 6. Message frequency varies. Message and data rates may apply. Reply STOP to opt out and HELP for help. Donations may be solicited. Consent is not a condition of donating, volunteering, or receiving campaign information. View our <Link to="/terms-and-conditions" className="font-bold text-[#0b2f58] underline">Terms and Conditions</Link> and <Link to="/privacy-policy" className="font-bold text-[#0b2f58] underline">Privacy Policy</Link>.
          </span>
        </label>

        <button type="submit" disabled={submitDisabled} className={`rounded-full px-8 py-4 text-sm font-extrabold uppercase tracking-[0.16em] shadow-lg transition ${submitDisabled ? "bg-gray-300 text-gray-500" : "bg-[#c0352b] text-white hover:bg-[#9f261f]"}`}>
          {status === "submitting" ? "Submitting..." : "Submit"}
        </button>
        {status === "success" && <p className="font-semibold text-green-700">Thank you. You are connected with the campaign.</p>}
        {status === "error" && <p className="font-semibold text-[#c0352b]">Something went wrong. Please try again or email {campaign.email}.</p>}
      </form>
    </div>
  );
}

function Footer() {
  return (
    <footer className="border-t-4 border-[#c0352b] bg-[#08213d] px-4 py-12 text-white sm:px-6 lg:px-8">
      <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[1.2fr_0.8fr]">
        <div>
          <p className="text-sm font-extrabold uppercase tracking-[0.2em] text-[#f7d77a]">Jason Hardcastle for Galveston City Council District 6</p>
          <p className="mt-4 max-w-2xl text-white/75">
            Political advertising paid for by Jason Hardcastle for Galveston City Council District 6.
          </p>
          <div className="mt-6 flex flex-wrap gap-4 text-sm font-semibold text-white/80">
            <Link to="/privacy-policy" className="hover:text-[#f7d77a]">Privacy Policy</Link>
            <Link to="/terms-and-conditions" className="hover:text-[#f7d77a]">Terms & Conditions</Link>
            <Link to="/contact" className="hover:text-[#f7d77a]">Contact</Link>
          </div>
        </div>
        <div className="lg:text-right">
          <a href={`mailto:${campaign.email}`} className="block text-lg font-bold text-white hover:text-[#f7d77a]">{campaign.email}</a>
          <p className="mt-2 text-white/75">{campaign.location}</p>
          <div className="mt-5 flex items-center gap-4 lg:justify-end">
            <a href={campaign.facebookUrl} target="_blank" rel="noopener noreferrer" className="rounded-full bg-white p-3 text-[#1877F2] transition hover:bg-[#f7d77a]" aria-label="Jason Hardcastle campaign Facebook">
              <FacebookIcon />
            </a>
          </div>
          <p className="mt-6 text-sm text-white/60">&copy; 2026 Jason Hardcastle for Galveston City Council District 6.</p>
        </div>
      </div>
    </footer>
  );
}

function FacebookIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" width="26" height="26" aria-hidden="true">
      <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5.02 3.66 9.18 8.44 9.94v-7.03H7.9v-2.91h2.54V9.84c0-2.52 1.49-3.91 3.77-3.91 1.09 0 2.23.2 2.23.2v2.47h-1.25c-1.24 0-1.63.78-1.63 1.57v1.89h2.77l-.44 2.91h-2.33V22C18.34 21.24 22 17.08 22 12.06z" />
    </svg>
  );
}

export default App;
